/* Angular + external */
import { ChangeDetectionStrategy, Component, QueryList, Renderer2 } from "@angular/core";
import { ViewChildren } from "@angular/core";
import { ChangeDetectorRef } from "@angular/core";
/***********************/
/* BFX */
// multi-asset
import * as CubiqTS from "shared/cubiq-model-multi-asset";
// cubiq-services
import {
  MarketDataEventTransformer,
  RateSubscriptionPromiseService,
  TraderGroupPromiseLookupService,
  RateCache,
  CcyPairPromiseLookupService,
  RatePromiseService,
  CurrencyPairCalendarPromiseLookupService,
  SystemConfigPromiseService,
  BranchLookupService,
  ClientLookupService,
} from "shared/cubiq-services";

// global-services

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { TranslateService } from "@ngx-translate/core";
// order-entry services
import { OESettingsService } from "../services/basic/oe-settings.service";
import { OECoordinatorService } from "../services/oe-coordinator.service";
import { OEApiService } from "../services/oe-api.service";
import { OEUtilityService } from "../services/basic/oe-utility.service";
// order-entry  models
import { OEInputLockStateItemModel } from "../data/models/oe-locking-profile.model";
//  order-entry  enums
import { OENInputControl } from "../data/enums/oe-input-control.enum";
import { OENLockedState } from "../data/enums/oe-locked-state.enum";
// interface controls components
// interface controls enums
import { ICNInputControlState } from "./controls/models/bfx-interface-controls.enums";
// interface controls models
import { ICAutocompleteGenericModel } from "./controls/models/bfx-interface-controls.models";
import { OEDLoadingDataModel } from "../data/models/oe-loading-data.model";
import { OESpotEntryComponent } from "./oe-panel-spot-entry.component";
import { OEPanelGammaModel } from "../data/models/oe-panel-gamma.model";
import { OELoggerService } from "../services/basic/oe-logger.service";
import { BfxConfigService } from "../../../shared/auth-services";
import { OEInputModel } from "../data/models/oe-input.model";
import { OENPanel } from "../data/enums/oe-panel.enum";
import { OENOperationMode } from "../data/enums/oe-operation-mode.enum";
import { OEDatasetService } from "app/order-entry/services/basic/oe-dataset.service";
import { OEModelLockingService } from "../services/model/oe-model-locking.service";
import { BfxInputControlStateEnum } from "app/bfx-interface-controls/models/bfx-interface-controls.enums";
import { NumberSpinnerComponent } from "shared/base-components/number-spinner.component";
import { OEAdapterForwardService } from "../services/adapter/oe-adapter-forward.service";
import { OEModelGetterService } from "../services/model/oe-model-getter.service";

const LABEL_REFERENCE_MID: string = "order_entry.panel.label.reference_mid";
const ERROR_MIN_RUNGS: string = "oe.entry.gamma.minRungs";

@Component({
  selector: "oe-panel-gamma-entry",
  templateUrl: "./html/oe-panel-gamma-entry.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class OEGammaEntryComponent extends OESpotEntryComponent {
  @ViewChildren("inputDelta") deltaControl: QueryList<NumberSpinnerComponent>;
  @ViewChildren("inputRungsAbove")
  rungsAboveControl: QueryList<NumberSpinnerComponent>;
  @ViewChildren("inputRungsBelow")
  rungsBelowControl: QueryList<NumberSpinnerComponent>;

  public data: OEPanelGammaModel = new OEPanelGammaModel();

  /**********************************************************************/
  /* CONSTRUCTOR  */
  constructor(
    translate: TranslateService,
    settings: OESettingsService,
    coordinator: OECoordinatorService,
    traderGroupService: TraderGroupPromiseLookupService,
    getter: OEModelGetterService,
    forwardAdapter: OEAdapterForwardService,
    utility: OEUtilityService,
    api: OEApiService,
    cd: ChangeDetectorRef,
    //
    rateService: RatePromiseService,
    rateCache: RateCache,
    rateTransformer: MarketDataEventTransformer,
    rateSubscriptionService: RateSubscriptionPromiseService,
    currencyPairService: CcyPairPromiseLookupService,
    calendarService: CurrencyPairCalendarPromiseLookupService,
    oeLogger: OELoggerService,
    public renderer2: Renderer2,
    sysConfigService: SystemConfigPromiseService,
    branchLookupService: BranchLookupService,
    configService: BfxConfigService,
    protected clientLookupService: ClientLookupService,
    datasetService: OEDatasetService,
    protected locker: OEModelLockingService,
  ) {
    super(
      translate,
      settings,
      coordinator,
      traderGroupService,
      getter,
      forwardAdapter,
      utility,
      api,
      cd,
      rateService,
      rateCache,
      rateTransformer,
      rateSubscriptionService,
      currencyPairService,
      calendarService,
      oeLogger,
      renderer2,
      sysConfigService,
      branchLookupService,
      configService,
      clientLookupService,
      datasetService,
    );

    this.oeLogger = oeLogger.for(__dirname);
  }

  /**********************************************************************/
  /* INITIALIZATION */

  protected initializeLabelTranslations(): void {
    super.initializeLabelTranslations();

    this.data.requestPrice.label = this.translate.instant(LABEL_REFERENCE_MID);
    this.data.delta.label = this.translate.instant(this.data.delta.labelKey);
    this.data.rungsAbove.label = this.translate.instant(this.data.rungsAbove.labelKey);
    this.data.rungsBelow.label = this.translate.instant(this.data.rungsBelow.labelKey);
  }

  protected initializeSettings(): void {
    super.initializeSettings();

    this.data.rungsAbove.maxRungs = this.settings.maximumGammaRungs;
    this.data.rungsAbove.max = this.data.rungsAbove.maxRungs;
    this.data.rungsBelow.max = this.data.rungsAbove.maxRungs;
  }

  /**********************************************************************/
  /* RESET */

  // handleResetAllControls
  protected resetData(): void {
    super.resetData();

    this.data.delta.minValue = 0;
    this.data.delta.precision = 1;
    this.data.delta.value = this.data.delta.oldValue = 0.0;
    this.data.rungsAbove.value = this.data.rungsAbove.oldValue = 0;
    this.data.rungsBelow.value = this.data.rungsBelow.oldValue = 0;

    if (
      this.rungsAboveControl != null &&
      this.rungsAboveControl.first != null &&
      this.rungsAboveControl.first.writeValue != null
    ) {
      this.rungsAboveControl.first.writeValue(this.data.rungsAbove.value);
    }

    if (
      this.rungsBelowControl != null &&
      this.rungsBelowControl.first != null &&
      this.rungsBelowControl.first.writeValue != null
    ) {
      this.rungsBelowControl.first.writeValue(this.data.rungsBelow.value);
    }
  }

  protected resetLocking(): void {
    super.resetLocking();

    this.data.delta.lockState = OENLockedState.UNLOCKED;
    this.data.rungsAbove.lockState = OENLockedState.UNLOCKED;
    this.data.rungsBelow.lockState = OENLockedState.UNLOCKED;
  }

  /**********************************************************************/
  /* LOAD */

  protected load(data: OEDLoadingDataModel): void {
    super.load(data);

    // On duplicate activate customize by default
    if (this.operationMode === OENOperationMode.DUPLICATE) {
      this.coordinator.cmdLoadGammaFromDuplicate(this.refId);
      let delta: string = this.api.getGammaDelta(this.refId);
      let deltaValue: number = delta != null && !Number.isNaN(delta) ? Number(delta) : null;
      this.data.delta.value = this.data.delta.oldValue = deltaValue;
      this.data.delta.duplicateDelta = true;
    }
  }

  protected loadFinancials(item: any, containsCheck: any, data: OEDLoadingDataModel): void {
    super.loadFinancials(item, containsCheck, data);

    switch (item.input) {
      case OENInputControl.DELTA:
        this.data.delta.value = this.data.delta.oldValue = item.value;
        break;
      case OENInputControl.RUNGS_ABOVE:
        this.data.rungsAbove.value = this.data.rungsAbove.oldValue = item.value;
        break;
      case OENInputControl.RUNGS_BELOW:
        this.data.rungsBelow.value = this.data.rungsBelow.oldValue = item.value;
        break;
    }
  }

  /**********************************************************************/
  /* LOCKING */
  protected assignLockStateToData(lockStateItem: OEInputLockStateItemModel): void {
    switch (lockStateItem.input) {
      case OENInputControl.DELTA:
        this.data.delta.lockState = lockStateItem.lockState;
        break;
      case OENInputControl.RUNGS_ABOVE:
        this.data.rungsAbove.lockState = lockStateItem.lockState;
        break;
      case OENInputControl.RUNGS_BELOW:
        this.data.rungsBelow.lockState = lockStateItem.lockState;
        break;
      default:
        super.assignLockStateToData(lockStateItem);
        break;
    }
  }

  protected updateLocking(): void {
    if (!this.control.lockAllFg) {
      this.data.delta.disabled = this.data.delta.lockState === OENLockedState.LOCKED;
      this.data.rungsAbove.disabled = this.data.rungsAbove.lockState === OENLockedState.LOCKED;
      this.data.rungsBelow.disabled = this.data.rungsBelow.lockState === OENLockedState.LOCKED;
    } else {
      this.data.delta.disabled = true;
      this.data.rungsAbove.disabled = true;
      this.data.rungsBelow.disabled = true;
    }

    super.updateLocking();
  }

  protected getTriggers(): Array<ICAutocompleteGenericModel> {
    let triggers: Array<ICAutocompleteGenericModel> = [];
    triggers.push(this.data.trigger.spotTrigger);
    return triggers;
  }

  /**********************************************************************/
  /* DATASET  */

  /**
   * Load all currency pairs from the replicate provided base lookup service
   * and fill in the appropriate currency pair values (inverted too, where applicable)
   * in the data model
   */
  protected currencyPairsGetDataset(): void {
    let dataset = this.api.getGammaCcyPairsDataset();
    if (dataset != null && dataset instanceof Array && dataset.length > 0) {
      this.data.ccyPairDatasets.sptFwdDataset = dataset.slice();
      this.data.currencyPair.dataset = dataset.slice();
    } else {
      logger.out.error(
        "ERROR: oeSpotEntry.getGammaCcyPairsDataset(): CcyPairs dataset load failed!",
      );
      this.data.ccyPairDatasets.sptFwdDataset = [];
      this.data.currencyPair.dataset = [];
    }
  }

  protected setCurrencyPair(productCode: string): boolean {
    let val = super.setCurrencyPair(productCode);

    if (productCode) {
      this.reEvaluateTrigger(null);
    }

    if (this.data.currencyPair.model instanceof CubiqTS.CurrencyPairImpl) {
      let currencyPair: CubiqTS.CurrencyPairImpl = this.data.currencyPair.model;

      let oldPrecision: number = this.data.delta.precision;
      this.data.delta.precision = this.getAdditionalPrecision();
      this.data.delta.step = Math.pow(10, -this.data.delta.precision);
      let deltaChanged: boolean = false;

      if (
        oldPrecision != null &&
        this.data.delta.precision != oldPrecision &&
        this.data.delta.value != null &&
        !Number.isNaN(this.data.delta.value)
      ) {
        this.data.delta.value = this.data.delta.oldValue = Number(
          Number(this.data.delta.value).toFixed(this.data.delta.precision),
        );
        deltaChanged = true;
      }

      if (currencyPair.minimumGammaDeltaPips && currencyPair.minimumGammaDeltaPips.toNumber) {
        this.data.delta.minValue = currencyPair.minimumGammaDeltaPips.toNumber();

        // If delta is less than minimum set the value to the minimum
        if (
          (this.data.delta.value == null ||
            (!Number.isNaN(this.data.delta.value) &&
              Number(this.data.delta.value) < this.data.delta.minValue)) &&
          !this.data.delta.duplicateDelta
        ) {
          this.data.delta.value = this.data.delta.oldValue = this.data.delta.minValue;
          deltaChanged = true;
        } else {
          this.data.delta.duplicateDelta = false;
        }
      }

      if (deltaChanged) {
        this.onDeltaChanged(this.data.delta.value);
      }
    }

    return val;
  }

  protected handleInvalidInput(inputError: OEInputModel): void {
    super.handleInvalidInput(inputError);

    if (inputError != null && inputError.panel === OENPanel.PANEL_FINANCIALS) {
      switch (inputError.input) {
        case OENInputControl.DELTA:
          if (this.control.initialLoadingComplete) {
            this.data.delta.externalState = ICNInputControlState.INVALID;
            if (this.deltaControl != null) {
              this.deltaControl.forEach((c) => c.changeState(BfxInputControlStateEnum.INVALID));
            }
          }
          break;
        case OENInputControl.RUNGS_ABOVE:
          if (this.control.initialLoadingComplete) {
            this.rungsAboveControl.first.errorMsg = this.translate.instant(ERROR_MIN_RUNGS);
          }
          break;
        case OENInputControl.RUNGS_BELOW:
          if (this.control.initialLoadingComplete) {
            this.rungsBelowControl.first.errorMsg = this.translate.instant(ERROR_MIN_RUNGS);
          }
          break;
      }
    }
  }

  public onDeltaChanged(value: any): void {
    this.cd.markForCheck();

    if (this.validateDelta(value)) {
      this.data.delta.oldValue = this.data.delta.value;
      this.data.delta.externalState = null;
      this.api.updateDelta(this.refId, value);
    } else {
      this.data.delta.value = this.data.delta.oldValue;
      this.deltaControl.first.writeValue(this.data.delta.value);
    }
  }

  public onOrderTypeChange($event: any, fromControl: boolean = false): void {
    super.onOrderTypeChange($event, fromControl);

    if (this.data.delta.value != null) {
      try {
        this.api.updateDelta(this.refId, this.data.delta.value + "");
      } catch (e) {
        logger.out.error(`${this.constructor.name}: Failed to update gamma panel ${e}`);
      }
    }
  }

  public onFixedCcyToggleChange($event: any): void {
    super.onFixedCcyToggleChange($event);

    if (this.data.delta.value != null) {
      try {
        this.api.updateDelta(this.refId, this.data.delta.value + "");
      } catch (e) {
        logger.out.error(`${this.constructor.name}: Failed to update gamma panel ${e}`);
      }
    }
  }

  public onRungsAboveChanged(value: any): void {
    this.cd.markForCheck();

    if (this.validateRungsAbove(value)) {
      this.data.rungsAbove.oldValue = this.data.rungsAbove.value;
      this.rungsAboveControl.first.errorMsg = null;
      this.rungsBelowControl.first.errorMsg = null;
      this.api.updateRungsAbove(this.refId, this.data.rungsAbove.value);
    } else {
      this.data.rungsAbove.value = this.data.rungsAbove.oldValue;
      this.rungsAboveControl.first.writeValue(this.data.rungsAbove.value);
    }

    if (!Number.isNaN(this.data.rungsAbove.value)) {
      this.data.rungsBelow.max = this.data.rungsAbove.maxRungs - Number(this.data.rungsAbove.value);
    }
  }

  public onRungsBelowChanged(value: any): void {
    this.cd.markForCheck();

    if (this.validateRungsBelow(value)) {
      this.data.rungsBelow.oldValue = this.data.rungsBelow.value;
      this.rungsAboveControl.first.errorMsg = null;
      this.rungsBelowControl.first.errorMsg = null;
      this.api.updateRungsBelow(this.refId, this.data.rungsBelow.value);
    } else {
      this.data.rungsBelow.value = this.data.rungsBelow.oldValue;
      this.rungsBelowControl.first.writeValue(this.data.rungsBelow.value);
    }

    if (!Number.isNaN(this.data.rungsBelow.value)) {
      this.data.rungsAbove.max = this.data.rungsAbove.maxRungs - Number(this.data.rungsBelow.value);
    }
  }

  protected validateDelta(value: number): boolean {
    let valid: boolean = true;

    if (value == null || Number.isNaN(value)) {
      valid = false;
    } else if (this.data.currencyPair.model instanceof CubiqTS.CurrencyPairImpl) {
      let currencyPair: CubiqTS.CurrencyPairImpl = this.data.currencyPair.model;

      if (
        currencyPair.minimumGammaDeltaPips &&
        currencyPair.minimumGammaDeltaPips.toNumber &&
        value < currencyPair.minimumGammaDeltaPips.toNumber()
      ) {
        valid = false;
      }
    }

    return valid;
  }

  protected validateRungsAbove(value: number): boolean {
    let valid: boolean = true;

    if (value == null || Number.isNaN(value)) {
      valid = false;
    } else {
      let rungsBelow: number = 0;
      let rungsAbove: number = Number(value);

      if (this.data.rungsBelow.value != null && !Number.isNaN(this.data.rungsBelow.value)) {
        rungsBelow = Number(this.data.rungsBelow.value);
      }

      let totalRungs: number = rungsAbove + rungsBelow;
      valid = totalRungs >= 0 && totalRungs <= this.data.rungsAbove.maxRungs;
    }

    return valid;
  }

  protected validateRungsBelow(value: number): boolean {
    let valid: boolean = true;

    if (value == null || Number.isNaN(value)) {
      valid = false;
    } else {
      let rungsBelow: number = Number(value);
      let rungsAbove: number = 0;

      if (this.data.rungsAbove.value != null && !Number.isNaN(this.data.rungsAbove.value)) {
        rungsAbove = Number(this.data.rungsAbove.value);
      }

      let totalRungs: number = rungsAbove + rungsBelow;
      valid = totalRungs >= 0 && totalRungs <= this.data.rungsAbove.maxRungs;
    }

    return valid;
  }

  protected getAdditionalPrecision(): number {
    let currencyPair: CubiqTS.CurrencyPairImpl = this.data.currencyPair.model;
    return this.data.currencyPair.inverted
      ? currencyPair.limitEntrySpotInvertDecimals - currencyPair.spotInvertDecimalsQty
      : currencyPair.limitEntrySpotDecimals - currencyPair.pipsQty;
  }
}
